/**
 * Kingdoms & City-States
 *
 * Special 1-tile settlements discovered throughout the world map.
 * Each unlocks diplomacy features: trading, quests, projects, guild members.
 */

export const KINGDOM_TYPES = {
  // Tier 1 - Early Game (Age 1-2)
  tradingPost: {
    id: 'tradingPost',
    name: 'Trading Post',
    icon: '🏪',
    type: 'city-state',
    tier: 1,
    minAge: 1,
    spawnWeight: 8,
    color: '#8b7355',
    description: 'A small frontier settlement focused on commerce.',
    flavorText: '"Welcome, traveler! Fresh goods from distant lands!"',
    specialties: ['Trading', 'Basic Resources'],
  },

  farmingVillage: {
    id: 'farmingVillage',
    name: 'Farming Village',
    icon: '🌾',
    type: 'city-state',
    tier: 1,
    minAge: 1,
    spawnWeight: 8,
    color: '#6b8e23',
    description: 'A peaceful agricultural community.',
    flavorText: '"Our harvest is plentiful this season!"',
    specialties: ['Food Production', 'Simple Quests'],
  },

  miningOutpost: {
    id: 'miningOutpost',
    name: 'Mining Outpost',
    icon: '⛏️',
    type: 'city-state',
    tier: 1,
    minAge: 2,
    spawnWeight: 6,
    color: '#696969',
    description: 'A rugged settlement built around rich mineral deposits.',
    flavorText: '"These mountains hold treasures beyond measure!"',
    specialties: ['Stone & Metal', 'Mining Projects'],
  },

  // Tier 2 - Mid Game (Age 3-5)
  merchantGuild: {
    id: 'merchantGuild',
    name: 'Merchant Guild',
    icon: '🏛️',
    type: 'city-state',
    tier: 2,
    minAge: 3,
    spawnWeight: 5,
    color: '#daa520',
    description: 'A prosperous trading hub controlled by wealthy merchants.',
    flavorText: '"Gold opens all doors, traveler."',
    specialties: ['Advanced Trading', 'Rare Goods', 'Guild Contracts'],
  },

  scholarEnclave: {
    id: 'scholarEnclave',
    name: 'Scholar\'s Enclave',
    icon: '📚',
    type: 'city-state',
    tier: 2,
    minAge: 3,
    spawnWeight: 5,
    color: '#4169e1',
    description: 'A center of learning and arcane study.',
    flavorText: '"Knowledge is the true currency of civilization."',
    specialties: ['Knowledge', 'Research Quests', 'Scholars'],
  },

  fortifiedTown: {
    id: 'fortifiedTown',
    name: 'Fortified Town',
    icon: '🏰',
    type: 'city-state',
    tier: 2,
    minAge: 4,
    spawnWeight: 4,
    color: '#708090',
    description: 'A walled settlement with a strong military presence.',
    flavorText: '"Our walls have never fallen. They never will."',
    specialties: ['Military Contracts', 'Defense Projects', 'Warriors'],
  },

  // Tier 3 - Late Game (Age 6-8)
  royalCapital: {
    id: 'royalCapital',
    name: 'Royal Capital',
    icon: '👑',
    type: 'kingdom',
    tier: 3,
    minAge: 6,
    spawnWeight: 3,
    color: '#9370db',
    description: 'The grand capital of a powerful kingdom.',
    flavorText: '"All roads lead to the throne."',
    specialties: ['Royal Quests', 'Major Projects', 'Elite Guild'],
  },

  arcaneAcademy: {
    id: 'arcaneAcademy',
    name: 'Arcane Academy',
    icon: '🔮',
    type: 'city-state',
    tier: 3,
    minAge: 6,
    spawnWeight: 3,
    color: '#8a2be2',
    description: 'A mystical institution of higher magical learning.',
    flavorText: '"Magic is art, science, and power entwined."',
    specialties: ['Magical Research', 'Arcane Traders', 'Mage Guild'],
  },

  dragonport: {
    id: 'dragonport',
    name: 'Dragonport',
    icon: '🐉',
    type: 'city-state',
    tier: 3,
    minAge: 7,
    spawnWeight: 2,
    color: '#dc143c',
    description: 'An exotic city where dragons and mortals coexist.',
    flavorText: '"Beware the skies—our allies are formidable."',
    specialties: ['Exotic Goods', 'Dragon Riders', 'Epic Quests'],
  },

  // Tier 4 - End Game (Age 9+)
  celestialCitadel: {
    id: 'celestialCitadel',
    name: 'Celestial Citadel',
    icon: '✨',
    type: 'kingdom',
    tier: 4,
    minAge: 9,
    spawnWeight: 1,
    color: '#ffd700',
    description: 'A legendary city blessed by divine powers.',
    flavorText: '"The heavens smile upon those who prove worthy."',
    specialties: ['Divine Blessings', 'Legendary Contracts', 'Celestial Guild'],
  },

  voidEnclave: {
    id: 'voidEnclave',
    name: 'Void Enclave',
    icon: '🌌',
    type: 'kingdom',
    tier: 4,
    minAge: 10,
    spawnWeight: 1,
    color: '#4b0082',
    description: 'A mysterious realm beyond normal reality.',
    flavorText: '"Time and space bend to our will here."',
    specialties: ['Cosmic Secrets', 'Reality Manipulation', 'Void Merchants'],
  },
};

/**
 * Get spawn weights for kingdoms/city-states based on current age
 */
export function getKingdomSpawnWeights(currentAge) {
  const weights = {};

  for (const [id, kingdom] of Object.entries(KINGDOM_TYPES)) {
    if (currentAge >= kingdom.minAge) {
      weights[id] = kingdom.spawnWeight;
    }
  }

  return weights;
}

/**
 * Select random kingdom based on weighted probabilities
 */
export function selectRandomKingdom(weights) {
  const entries = Object.entries(weights);
  if (entries.length === 0) return null;

  const totalWeight = entries.reduce((sum, [, weight]) => sum + weight, 0);
  let random = Math.random() * totalWeight;

  for (const [kingdomId, weight] of entries) {
    random -= weight;
    if (random <= 0) return kingdomId;
  }

  return entries[0][0]; // Fallback
}
